import _ from "lodash";
import OneDrugManyPayerTypesChartControllerBase from "./one_drug_many_payer_types_chart_controller_base";
import shrinkWidgetToFit from "../../utilities/charts/shrink_widget_to_fit";
import {
  accumulateValuesFn,
  dataKeyForPayerType,
  toOneDecimal,
  percentString,
  withinOneDecimal,
} from "../../utilities/charts/chart_utils";
import showPayersModal from "../../utilities/charts/payers_modal";
import colors from "../../utilities/charts/colors";

export default class extends OneDrugManyPayerTypesChartControllerBase {
  static targets = ["title", "gridTitle", "gridFraction", "gridPercent", "gridChangeIndicator"];

  connect() {
    super.connect();
  }

  initializeParams() {
    const params = super.initializeParams();

    this.enabledTypes = ["Yes", "No", "N/I"];

    return params;
  }

  toggleCoverageMetricInternal() {
    this.renderChart();
  }

  togglePayerTypeInternal() {
    this.renderChart();
  }

  toggleDrugInternal() {
    this.renderChart();
  }

  updateShownPayersInternal() {
    this.renderChart();
  }

  toggleStepTherapyResponse(event) {
    const stepTherapyResponse = $(event.target).data("step-therapy-response");

    this.enabledTypes = _.xor(this.enabledTypes, [stepTherapyResponse]);

    this.renderChart();
  }

  renderChart() {
    this.setMainStats();
    this.renderBars();
    this.updateResponseVisibility();
    shrinkWidgetToFit(this.element, this.element.children[0]);
  }

  setMainStats() {
    $(this.titleTarget).text(`Requires Step Therapy for ${this.drug}?`);
  }

  renderBars() {
    const responses = ["Yes", "No", "N/I"];
    const barColor = this.colorsByCoverageMetric[this.coverageMetric];

    responses.forEach(response => {
      const target = this.gridTitleTargets.find(t => t.dataset.stepTherapyResponse === response);

      if (!target) return;

      const isEnabled = this.enabledTypes.includes(response);

      // Apply line-through style to disabled labels
      target.classList.toggle("line-through", !isEnabled);

      const { current, prior } = this.currentAndPriorDataForResponse(response);
      const { all: currentAll } = this.accumulateDataForShownPayers().current;
      const { all: priorAll } = this.accumulateDataForShownPayers().prior;

      // Update fraction display
      const fractionTarget = this.gridFractionTargets.find(
        t => t.dataset.stepTherapyResponse === response,
      );

      if (fractionTarget) {
        fractionTarget.innerText = isEnabled
          ? `${this.formatNumber(current.count)}/${this.formatNumber(currentAll)}`
          : "";
      }

      // Update percentage and bar
      const percentTarget = this.gridPercentTargets.find(
        t => t.dataset.stepTherapyResponse === response,
      );

      if (percentTarget) {
        const currentPercent = current.count / currentAll || 0;
        const priorPercent = prior.count / priorAll || 0;

        const mainPercent = this.showChangesFromPrevious
          ? Math.max(currentPercent, priorPercent)
          : currentPercent;

        const textElement = percentTarget.querySelector(".text");
        const barElement = percentTarget.querySelector(".bar");
        const barRemainderElement = percentTarget.querySelector(".bar-remainder");

        if (textElement) {
          textElement.innerText = isEnabled ? percentString(current.count, currentAll) : "";
        }

        if (barElement) {
          barElement.style.transform = isEnabled ? `scaleX(${mainPercent})` : `scaleX(0)`;
          barElement.style.backgroundColor = barColor;
        }

        if (barRemainderElement) {
          barRemainderElement.style.transform = isEnabled
            ? `scaleX(${1 - mainPercent})`
            : `scaleX(0)`;
          barRemainderElement.classList.toggle("hidden", currentAll === 0 || !isEnabled);
        }
      }

      // Update change indicator if showing changes
      if (this.showChangesFromPrevious) {
        const changeTarget = this.gridChangeIndicatorTargets.find(
          t => t.dataset.stepTherapyResponse === response,
        );

        if (changeTarget) {
          const currentPercent = current.count / currentAll || 0;
          const priorPercent = prior.count / priorAll || 0;
          const arrow = changeTarget.querySelector("svg");

          if (withinOneDecimal(priorPercent, currentPercent)) {
            changeTarget.title = null;
            if (arrow) arrow.classList.add("hidden");
          } else if (currentPercent > priorPercent) {
            changeTarget.title = `Increased ${toOneDecimal((currentPercent - priorPercent) * 100)}`;
            if (arrow) {
              arrow.classList.remove("hidden", "text-red");
              arrow.classList.add("text-green");
              arrow.style.transform = "rotate(0deg)";
            }
          } else {
            changeTarget.title = `Decreased ${toOneDecimal((priorPercent - currentPercent) * 100)}`;
            if (arrow) {
              arrow.classList.remove("hidden", "text-green");
              arrow.classList.add("text-red");
              arrow.style.transform = "rotate(180deg)";
            }
          }
        }
      }
    });
  }

  currentAndPriorDataForResponse(response) {
    let currentCount = 0;

    let priorCount = 0;

    this.payerTypesShown.forEach(payerType => {
      const currentData = this.params[this.drug]?.[payerType];
      const priorData = this.params.previous_data?.[this.drug]?.[payerType];

      const accumulate = accumulateValuesFn(payerType, this.coverageMetric);

      if (currentData && currentData[response]) {
        currentCount += accumulate(currentData[response]);
      }

      if (priorData && priorData[response]) {
        priorCount += accumulate(priorData[response]);
      }
    });

    return {
      current: { count: currentCount },
      prior: { count: priorCount },
    };
  }

  accumulateDataForShownPayers() {
    const output = {
      prior: { yes: 0, all: 0 },
      current: { yes: 0, all: 0 },
    };

    this.payerTypesShown.forEach(payerType => {
      const currentData = this.params[this.drug]?.[payerType];
      const priorData = this.params.previous_data?.[this.drug]?.[payerType];

      const accumulate = accumulateValuesFn(payerType, this.coverageMetric);

      if (currentData) {
        const currentYes = this.enabledTypes.includes("Yes") ? currentData.Yes || [] : [];
        const currentAll = _.flatten(this.enabledTypes.map(type => currentData[type] || []));

        output.current.yes += accumulate(currentYes);
        output.current.all += accumulate(currentAll);
      }

      if (priorData) {
        const priorYes = this.enabledTypes.includes("Yes") ? priorData.Yes || [] : [];
        const priorAll = _.flatten(this.enabledTypes.map(type => priorData[type] || []));

        output.prior.yes += accumulate(priorYes);
        output.prior.all += accumulate(priorAll);
      }
    });

    return output;
  }

  updateResponseVisibility() {
    // All responses are always visible for step therapy charts
  }

  formatNumber(count) {
    const suffix = this.coverageMetric === "lives" ? "m" : "";

    return `${toOneDecimal(count)}${suffix}`;
  }

  get colorsByCoverageMetric() {
    return {
      payers: colors.magenta,
      lives: colors.teal,
    };
  }

  onBarClick(event) {
    const stepTherapyResponse = $(event.target).data("step-therapy-response");

    const title = `Payers with ${stepTherapyResponse} for ${this.drug} Step Therapy?`;

    const payerIds = {};
    const extraParams = {};

    this.payerTypesShown.forEach(payerType => {
      const normalizedPayerType = dataKeyForPayerType(payerType);

      const payerIdsFor = dataset =>
        dataset?.[this.drug]?.[payerType]?.[stepTherapyResponse]?.map(pl => pl.payer) || [];

      if (this.showChangesFromPrevious) {
        const currentPayers = payerIdsFor(this.params);
        const priorPayers = payerIdsFor(this.params.previous_data);

        if (currentPayers.length > 0 || priorPayers.length > 0) {
          payerIds[normalizedPayerType] = _.union(currentPayers, priorPayers);

          const changedPayers = _.xor(currentPayers, priorPayers);

          if (changedPayers.length > 0) {
            extraParams.changedCoverage = extraParams.changedCoverage || {};
            extraParams.changedCoverage[normalizedPayerType] = changedPayers.map(payer => ({
              payer,
              drug: this.drug,
            }));
          }
        }
      } else {
        const payers = payerIdsFor(this.params);

        if (payers.length > 0) {
          payerIds[normalizedPayerType] = payers;
        }
      }
    });

    if (this.showChangesFromPrevious) {
      extraParams.previousSnapshotSetId = this.params.previous_snapshot_set.id;
    }

    showPayersModal(this.drug, title, payerIds, extraParams);
  }

  onBarRemainderClick(event) {
    const stepTherapyResponse = $(event.target).data("step-therapy-response");
    const otherResponses = ["Yes", "No", "N/I"].filter(r => r !== stepTherapyResponse);

    const title = `Payers without ${stepTherapyResponse} for ${this.drug} Step Therapy?`;

    const payerIds = {};
    const extraParams = {};

    this.payerTypesShown.forEach(payerType => {
      const normalizedPayerType = dataKeyForPayerType(payerType);

      const payerIdsFor = dataset =>
        _.flatten(
          otherResponses.map(
            response => dataset?.[this.drug]?.[payerType]?.[response]?.map(pl => pl.payer) || [],
          ),
        );

      if (this.showChangesFromPrevious) {
        const currentPayers = payerIdsFor(this.params);
        const priorPayers = payerIdsFor(this.params.previous_data);

        if (currentPayers.length > 0 || priorPayers.length > 0) {
          payerIds[normalizedPayerType] = _.union(currentPayers, priorPayers);

          const changedPayers = _.xor(currentPayers, priorPayers);

          if (changedPayers.length > 0) {
            extraParams.changedCoverage = extraParams.changedCoverage || {};
            extraParams.changedCoverage[normalizedPayerType] = changedPayers.map(payer => ({
              payer,
              drug: this.drug,
            }));
          }
        }
      } else {
        const payers = payerIdsFor(this.params);

        if (payers.length > 0) {
          payerIds[normalizedPayerType] = payers;
        }
      }
    });

    if (this.showChangesFromPrevious) {
      extraParams.previousSnapshotSetId = this.params.previous_snapshot_set.id;
    }

    showPayersModal(this.drug, title, payerIds, extraParams);
  }
}
